import { BBPlayerAPI } from './../../../interfaces/bb-player-api';
import { DialogDefinition, NoticeDefinition } from './../../../services/overlay.service';
import { AssetService } from './../../../services/asset.service';
import { Component, Input, SimpleChanges, ViewChild, TemplateRef, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Mediaclip } from '../../../interfaces/mediaclip';
import { BbTableService } from '../../../services/bb-table.service';
import { BbFile, UploadService } from '../../../services/upload.service';
import { SapiService, SapiEntity, SapiError } from '../../../services/sapi.service';
import { PublicationService } from '../../../services/publication.service';
import { Publication } from '../../../interfaces/publication';
import { Subscription, ReplaySubject, Subject, Observable, of } from 'rxjs';
import { ClipboardService } from 'ngx-clipboard';
import { ModalDefinition, OverlayService } from '../../../services/overlay.service';
import { TranslateService } from '../../../../node_modules/@ngx-translate/core';
import { MediaclipJobDefinition, TranscodingService } from '../../../services/transcoding.service';

const MAX_TABLE_ROWS = 10;

@Component({
	selector: 'mediaclip-details-resources',
	templateUrl: './mediaclip-details-resources.component.html',
	styleUrls: ['./mediaclip-details-resources.component.scss']
})

export class MediaclipDetailsResourcesComponent implements OnInit, OnChanges, OnDestroy {

	@Input() mediaclip: Mediaclip;
	@Input() form: NgForm;
	@Input() isActive: boolean;

	@ViewChild('trimTemplate') trimTemplate: TemplateRef<any>;

	assetTitles: any[];
	assetRows: any[];

	assetsPage: number;
	playerAssets: string[];

	baseUrl: string;
	playerApi: BBPlayerAPI;
	mediaclipJobDefinitions: MediaclipJobDefinition[];
	mediaFormats: any[];

	subscriptions: Subscription;
	processedSrc = '';

	technicalInfo: SapiEntity;
	technicalInfoSubject: ReplaySubject<SapiEntity>;
	uploading: boolean;

	importedMediaclip: boolean;
	importedMediaclipUrl: string;

	filter: any;

	hasMp4Asset = false;
	allowTrimModalClose = true;
	trimModal: ModalDefinition;

	constructor(
		private bbTableService: BbTableService,
		private sapiService: SapiService,
		private overlayService: OverlayService,
		private publicationService: PublicationService,
		private uploadService: UploadService,
		private transcodingService: TranscodingService,
		private translate: TranslateService,
		private assetService: AssetService,
		private clipboard: ClipboardService) {
		this.assetsPage = 1;
		this.bbTableService
			.setConfigurable(false)
			.setLimit(10);

		this.subscriptions = new Subscription();
		this.technicalInfoSubject = new ReplaySubject<SapiEntity>(1);
	}

	ngOnInit() {

		this.subscriptions.add(this.publicationService.getData().subscribe((publicationData: Publication) => {
			if (publicationData) {
				this.processedSrc = this.assetService.getAssetUrl(this.mediaclip, null, true);
				this.baseUrl = publicationData.baseurl;
			}
		}));

		this.subscriptions.add(this.transcodingService.mediaclipJobDefinitionsSubject.subscribe((mediaclipJobDefinitions: MediaclipJobDefinition[]) => {
			this.mediaclipJobDefinitions = mediaclipJobDefinitions;
			this.mediaFormats = this.mediaclipJobDefinitions
				.map((mediaclipJobDefinition) => mediaclipJobDefinition.mediaformat)
				.filter((mediaFormat, index, self) => self.indexOf(mediaFormat) === index);
		}));

		this.subscriptions.add(this.uploadService.startedObservable.subscribe(() => {
			this.uploading = true;
		}));

		this.subscriptions.add(this.uploadService.cancelledObservable.subscribe(() => {
			this.uploading = false;
			this.mediaclip.uploading = false;
			this.sapiService.update('mediaclip', this.mediaclip).subscribe();
		}));

		this.subscriptions.add(this.uploadService.completedObservable.subscribe(() => {
			this.uploading = false;
			this.mediaclip.uploading = false;
			this.sapiService.update('mediaclip', this.mediaclip).subscribe();
		}));

		this.filter = this.filterAssetUploads.bind(this);
		if (this.mediaclip && this.mediaclip.createdBy) {
			this.sapiService.get('mediaclip', this.mediaclip.id, 'mediainfo').subscribe((techInfo: SapiEntity) => this.technicalInfo = techInfo);

			this.hasMp4Asset = this.mediaclip && this.mediaclip.assets
				&& this.mediaclip.assets.filter(asset => asset.mediatype === 'MP4_MAIN' && (/\.mp4$/i).test(asset.src)).length > 0;
		}
		this.processedSrc = this.assetService.getAssetUrl(this.mediaclip, null, true);
		this.assetPageChanged(this.assetsPage);
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges && simpleChanges.mediaclip) {
			this.technicalInfoSubject = new ReplaySubject<SapiEntity>(1);
			this.assetTitles = [];
			this.assetRows = [];
			if (this.mediaclip && this.mediaclip.assets) {

				if (this.mediaclip.sourceid && this.mediaclip.sourceid.startsWith('imported-from:')) {
					this.importedMediaclip = true;
					this.importedMediaclipUrl = this.mediaclip.sourceid.replace('imported-from:', '');
				}

				this.assetTitles = this.mediaclip.assets.map((asset) => {
					const height = parseInt(asset.height, 10);
					const title = (height > 0) ? (Math.ceil(height / 120) * 120).toString() + 'p' : '';
					return {
						id: asset.id,
						title,
						bandwidth: parseInt(asset.bandwidth, 10)
					};
				});

				this.assetTitles.sort((a, b) => b.bandwidth - a.bandwidth);

				this.assetTitles = this.assetTitles.map((assetTitle) => {
					const duplicateTitles = this.assetTitles
						.filter((assetTitleDuplicate) => assetTitle.title === assetTitleDuplicate.title && assetTitle.title.indexOf(' ') === -1);

					if (duplicateTitles && duplicateTitles.length > 1) {
						if (duplicateTitles[0].bandwidth > duplicateTitles[1].bandwidth) {
							duplicateTitles[0].title += ' high';
							duplicateTitles[1].title += ' low';
						} else {
							duplicateTitles[0].title += ' low';
							duplicateTitles[1].title += ' high';
						}
					}
					return assetTitle;
				});

				this.hasMp4Asset = this.mediaclip && this.mediaclip.assets
					&& this.mediaclip.assets.filter(asset => asset.mediatype === 'MP4_MAIN' && (/\.mp4$/i).test(asset.src)).length > 0;
				this.assetPageChanged(this.assetsPage);
			}
			this.processedSrc = this.assetService.getAssetUrl(this.mediaclip, null, true);
		}
	}

	onTrimchange(trimStatus: any) {
		this.allowTrimModalClose = !trimStatus.isTrimmed;
	}

	onTrimClick() {
		this.trimModal = {
			icon: 'cut',
			title: this.translate.instant('TRIM_MEDIA'),
			allowClose: () => this.allowTrimModalClose,
			component: {
				name: 'custom-content',
				templateRef: this.trimTemplate
			}
		};

		this.overlayService.createModal(this.trimModal);
	}

	onTrimSave(clip) {
		this.overlayService.removeModal(this.trimModal);
		const isThisClip = clip?.id === this.mediaclip.id;
		const notice: NoticeDefinition = {
			icon: 'info-circle',
			title: this.translate.instant('TRIMMING_STARTED'),
			type: 'info',
			contents: this.translate.instant(isThisClip ? 'TRIMMING_STARTED_MESSAGE' : 'TRIMMING_STARTED_MESSAGE_NEW')
		};
		this.overlayService.createNotice(notice);
	}

	onTrimCancel() {
		this.overlayService.removeModal(this.trimModal);
	}

	uploadText(includeTrim = false): string {
		if (this.mediaclip?.mediatype === 'image') {
			return this.translate.instant('REPLACE_SOURCE_IMAGE');
		} else if (this.mediaclip && (this.mediaclip.assets || this.mediaclip.hasRunningJobs)) {
			return this.translate.instant((includeTrim && this.mediaclip.assets?.length ? 'TRIM_' : '') + 'REPLACE_' + this.mediaclip.mediatype.toUpperCase());
		} else {
			return this.translate.instant('UPLOAD_MEDIATYPE_FILE', { mediatype: this.mediaclip.mediatype || 'any' });
		}
	}

	showTechnicalInfo() {
		const modal: ModalDefinition = {
			icon: '',
			title: '',
			component: {
				name: 'bb-technical-info',
				inputs: {
					technicalInfo: this.technicalInfo
				}
			}
		};

		this.overlayService.createModal(modal);
	}

	onItemRemoved(items: any[], index: number) {
		items.splice(index, 1);
	}

	onAssetSaved(savedItem: any, items: any[], i: number) {
		const assetIndex = this.mediaclip.assets.findIndex(a => a.id === savedItem.id);
		if (assetIndex > -1) {
			this.mediaclip.assets[assetIndex] = savedItem;
			this.assetPageChanged(this.assetsPage);
		}
	}

	onAssetCanceled(cancelledItem: any, items: any[], i: number) {
		this.assetRows[i].data = cancelledItem;
		this.assetPageChanged(this.assetsPage);
	}

	addNewAsset() {
		this.assetRows.push({ id: 'new', mediaclipId: this.mediaclip.id, quickEditing: true, data: { status: 'active', 'media-clipid': this.mediaclip.id } });
	}

	filterAssetUploads(file: BbFile): boolean {
		if (this.mediaclip && this.mediaclip.mediatype === 'document') {
			return true;
		}
		return file.type.startsWith('audio/') || file.type.startsWith('video/');
	}

	downloadAsset(url: string) {
		if (url && url.length > 0) {
			const filename = url.substring(url.lastIndexOf('/') + 1);
			this.sapiService.downloadFromUrl(url, filename).subscribe();
		}
	}

	showPreview(assetId: string) {
		if (assetId) {
			const modal: ModalDefinition = {
				icon: 'film',
				title: (this.mediaclip.title || this.mediaclip.originalfilename),
				component: {
					name: 'embed-preview',
					inputs: {
						mediaclip: this.mediaclip,
						assetId
					}
				}
			};

			this.overlayService.createModal(modal);
		}
	}

	assetPageChanged(pageNumber: number) {
		if (this.mediaclip && this.mediaclip.assets) {
			this.assetsPage = pageNumber;
			const offset = (pageNumber - 1) * MAX_TABLE_ROWS;
			const end = offset + MAX_TABLE_ROWS;
			this.assetRows = this.mediaclip.assets.slice(offset, end);

			this.assetRows = this.assetRows.map((asset) => {
				const source = (asset) ? this.assetService.getAssetUrl(this.mediaclip, asset.src) : '';
				if (asset.mediatype === 'MP4_HLS' || asset.mediatype === 'MPD' ) {
					const label = (asset.mediatype === 'MP4_HLS') ? this.translate.instant('MP4_HLS') : this.translate.instant('MPD');
					return {
						id: asset.id,
						src: source,
						quickEditing: false,
						title : label,
						data: asset,
					};
				}

				const titles: any[] = this.assetTitles.filter((assetTitle) => assetTitle.id === asset.id);

				let title = '';
				if (titles.length > 0) {
					title = titles[0].title;
				}

				return {
					id: asset.id,
					src: source,
					quickEditing: false,
					title,
					data: asset
				};
			});
		} else {
			this.assetRows = [];
		}
	}

	copyUrlToClipboard(url: string) {
		this.clipboard.copyFromContent(url);
		this.overlayService.createToast(this.translate.instant('URL_COPIED_TO_CLIPBOARD'), 'copy');
	}

	startHlsTransmuxing() {
		// For now we restrict this function to live recordings
		if (this.mediaclip && this.mediaclip.sourceid && this.mediaclip.sourceid.startsWith('live_rec_')) {
			this.sapiService.get('mediaclip', this.mediaclip.id, 'transmuxHls')
				.subscribe(
					(res: SapiError) => {
						if (!res.error && res.code === '200') {
							this.overlayService.createToast(this.translate.instant('TRANSCODING_STARTED'));
							this.mediaclip.hasRunningJobs = true;
						} else {
							this.overlayService.createToast(this.translate.instant('ASSET_ALREADY_GENERATED'));
						}
					},
					(err: SapiError) => {
						this.overlayService.createToast(this.translate.instant('ASSET_ALREADY_GENERATED'));
					});
		} else {
			this.overlayService.createToast(this.translate.instant('OPERATION_NOT_SUPPORTED'));
		}
	}

	/**
	 * When this clip contains a Youtube export, warn the user
	 * arrow syntax for maintaining this when called from child
	 */
	checkYoutubeExport = (): Observable<any> => {
		if (!this.mediaclip.YouTube_id) {
			return of(true);
		}

		const result = new Subject<any>();

		const dialog: DialogDefinition = {
			icon: 'warning',
			title: this.translate.instant('EXPORTED_TO_YOUTUBE_REPLACE_WARNING'),
			contents: this.translate.instant('EXPORTED_TO_YOUTUBE_REPLACE_WARNING_DESC'),
			onConfirm: () => {
				result.next(true);
			}
		};

		this.overlayService.createDialog(dialog);
		return result;
	};

	trackBy(index: number, item: any) {
		return item.id || index;
	}
}
